import { BILLING_ROLES } from "./articleRoles";
import { isPlanDowngradesEnabled } from "../../Billing/planDowngrades";

const CATEGORY = "Billing";

// What a plan change does, as the Subscription page does it: the rules decided
// on 2026-10-15 while they are switched on (components/Billing/planDowngrades.js),
// otherwise the backend's behaviour today.
const PLAN_CHANGE_RULES = `What happens depends on the plan you choose:

- **A bigger plan** changes straight away. The difference between the two monthly prices is charged today, and the new price applies from your next billing date.
- **A smaller plan** changes when your contract ends: at the end of the month you've paid for, or at the end of your 12-month commitment. Until then nothing changes, and **Keep my current plan** cancels the change.
- **The same plan, from monthly to annual,** starts when the month you've paid for ends.
- **An annual plan** can't move to a monthly plan until its 12 months end.

A smaller plan can only be chosen when your active workspaces, and the active projects in each, fit it. Archive the ones you don't need first: choose **Archive** in a workspace's menu or on a project's row. Nothing archived is deleted, and you can restore it from **Archived workspaces** or **Archived projects** when your plan has room for it.

During a free trial, every change is made straight away and nothing is charged.`;

const PLAN_CHANGE_TODAY = `The plan changes straight away, and nothing is charged today. Your next invoice adds the new plan's price for the days left in the current period, less the unused part of your current plan, or takes the difference off if the new plan is cheaper. During a free trial, the trial carries on and the new plan's price applies when it ends.`;

const ANNUAL_PLAN_MOVES = isPlanDowngradesEnabled()
   ? "Until then it can move to a bigger annual plan, and a smaller or monthly plan starts when the 12 months end."
   : "Until then it can move to another annual plan, and to a monthly plan only in its last month.";

export const BILLING_ARTICLES = [
   {
      id: "manage-subscription",
      category: CATEGORY,
      title: "Manage your subscription",
      summary: "Change your plan, update your card, download invoices or cancel.",
      roles: BILLING_ROLES,
      related: ["enterprise-plan", "add-funds", "usage-statistics"],
      body: `
Only the organization owner manages billing. Click your avatar and choose **Subscription**.

At the top of the page you'll see your next payment date and the card on file. Below them are the available plans, then your invoices.

## Change your plan

1. Use the **Annual plan** / **Monthly plan** switch to compare prices. Both are charged every month: the annual plan costs less a month, for a 12-month commitment.
2. Click **Choose this plan** on the plan you want. Your current plan is marked **Current plan**.
3. If you have a promo code, enter it in **Enter promo code**.
4. Click **Continue**.

${isPlanDowngradesEnabled() ? PLAN_CHANGE_RULES : PLAN_CHANGE_TODAY}

Need prices and limits set to your organization? See [The Enterprise plan](#enterprise-plan).

## Add or change your card

Click **Add payment method**, or **Edit payment method** if a card is already saved. Enter the card details and save. Card details are handled by Stripe, our payment provider.

## Download invoices

Your invoices are listed under **Billing history**. Click **Download** on a row for one invoice, or **Download all** for all of them.

## Cancel your subscription

1. Click **Cancel my subscription**.
2. Tell us why, then click **Continue**.

Your subscription stays active until the end of the period you've paid for, and then doesn't renew. The page shows the end date under **Active until**.

An annual plan is a 12-month commitment. Cancelled before its last month, it stays active and is still charged every month until the 12 months end. ${ANNUAL_PLAN_MOVES}

## Refunds

Subscription payments aren't refunded: cancelling stops the next charges, and you keep your plan until the end of the period you've paid for. The only exception is a charge made in error, such as a duplicate charge or a charge after your cancellation took effect. Write to info@kaptechgroup.com and we'll refund it in full.
`
   },
   {
      id: "enterprise-plan",
      category: CATEGORY,
      title: "The Enterprise plan",
      summary:
         "Ask for a plan with prices and limits set for your organization, then take it up when it's ready.",
      roles: BILLING_ROLES,
      related: ["manage-subscription", "usage-statistics"],
      body: `
The Enterprise plan is for organizations that need more than the listed plans. Its prices, and how many workspaces and projects it allows, are agreed with our team for your organization. It has no free trial, and nothing is charged until you take up the plan we offer you.

## Ask for Enterprise

Ask for it from any of these places. Each time, use **Tell us what you need** to say how many workspaces, projects and users you need, whether you'd like monthly or annual billing, and anything else that matters to you.

- **When you sign up**: pick the **Enterprise** card on **Choose your package**, create your account, and click **Request Enterprise** on the review step.
- **On the Subscription page**, while you're on another plan: click **Request Enterprise** on the Enterprise card, then **Send request**. Your current plan carries on as before.
- **On the billing page**, when your organization has no plan: pick the **Enterprise** option under the plans and click **Request Enterprise**.

We send your request to our team and email you a confirmation. The Enterprise card then shows **Enterprise requested — we'll be in touch**.

> **Tip:** Don't want to wait? On the billing page you can pick one of the standard plans instead. That drops the Enterprise request.

## When your plan is ready

Our team contacts you to agree the prices and limits, then sets them up. You get an email: **Your KAPSurvey Enterprise plan is ready**.

- **If you have no plan yet**, sign in. The billing page shows **Your Enterprise plan is ready**, with your prices and limits. Choose **Annual plan** or **Monthly plan** and click **Subscribe**. If no card is saved, add one next. The plan starts with the first payment.
- **If you're on another plan**, open **Subscription**. The card **New Enterprise terms available** shows your offer. Choose **Annual plan** or **Monthly plan**, click **Choose this plan**, then **Continue**. The window tells you what is charged today and what later, the same way as any [plan change](#manage-subscription).

You choose monthly or annual billing at this point, with both prices in front of you.

## On the Enterprise plan

- The Subscription page shows your **Enterprise package** with your prices and limits. Prices are in the currency agreed with you.
- **Usage** shows your workspaces against the limit in your plan.
- When you reach your workspace or project limit, archive what you don't need, or ask us for more.
- When we agree new terms with you, they appear on the Subscription page as **New Enterprise terms available**. Choose them the same way. If only your limits change, at the same prices, they apply straight away.
- Monthly or annual billing, cancelling and invoices work as on any plan.
- To move from Enterprise to another plan, write to us at info@kaptechgroup.com.

If the Enterprise card on your Subscription page has no **Request Enterprise** button, click **Choose this plan** there and write to us at info@kaptechgroup.com.
`
   },
   {
      id: "add-funds",
      category: CATEGORY,
      title: "Add funds to your balance",
      summary: "Top up the balance that pays for sending surveys by email, SMS and WhatsApp.",
      roles: BILLING_ROLES,
      related: ["manage-subscription"],
      body: `
Your balance pays for sending surveys through campaigns, for example by email, SMS or WhatsApp. The price per recipient depends on the channel and the country. All amounts are in US dollars.

The balance is shown at the top of every page to the organization owner, Senior Managers and Project Managers. Only the owner can add funds.

## Add funds

1. Click your avatar and choose **Balance**.
2. Click **Add funds**.
3. Pick an amount, or type your own under **Or enter a custom amount**. The minimum is $1, in whole dollars.
4. Click **Confirm**.
5. Pay with your saved card. If no card is saved yet, add one first with **Add payment method**.

> **Tip:** Add $500 or more at once and you get a **+10% bonus** on top.

You'll see "Funds added successfully!" Sometimes the payment needs a moment, and you'll see that the funds will be added shortly.

## Purchase history and refunds

Every top-up is listed under **Purchase History**.

- Click **Download** for a top-up's invoice, or **Download all**.
- If a top-up can be refunded, its row shows **Refund**. Click it and confirm with **Yes, refund**. A refund can't be undone.

## When the balance runs low

If a campaign costs more than your balance, you'll see **Insufficient balance** and can't send it until you add funds. You can still save it as a draft.
`
   },
   {
      id: "usage-statistics",
      category: CATEGORY,
      title: "Check your usage",
      summary: "See storage, surveys, respondents, workspaces and projects at a glance.",
      roles: BILLING_ROLES,
      related: ["manage-subscription"],
      body: `
Click your avatar and choose **Usage** to open the **Account summary**.

Under **Usage statistics** you'll find:

- **Memory**: how much data storage is used and how much is left.
- **Surveys**, **Respondents** and **Data Collectors**: totals and monthly averages.
- **Workspaces**: how many you have against your plan's limit.
- **Projects**: how many projects you have.

Use **Select a workspace**, **Select a year**, **Select a month** or **Select a date range** to narrow the numbers down.
`
   }
];
